import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { Workshop, IWorkshopDate } from '../../types/workshop';
import api from '../../utils/api';
import { Category } from '../../types/category';
import { CATEGORY_OPTIONS } from '../../types/category';

interface WorkshopFormData {
  title: string;
  description: string;
  location: string;
  category: Category;
  level: 'beginner' | 'medium' | 'advanced';
  participantLimit: number;
  price: number;
  earlyBirdDate: string;
  earlyBirdDiscountPercentage: number;
  image: string;
  shouldHidePrice: boolean;
  active: boolean;
  priority: number;
  workshopDates: Array<{ date: string; duration: number }>;
}

export const WorkshopsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState<Workshop | null>(null);
  const [formData, setFormData] = useState<WorkshopFormData>({
    title: '',
    description: '',
    location: '',
    category: CATEGORY_OPTIONS[0].value,
    level: 'beginner',
    participantLimit: 0,
    price: 0,
    earlyBirdDate: '',
    earlyBirdDiscountPercentage: 0,
    image: '',
    shouldHidePrice: false,
    active: true,
    priority: 0,
    workshopDates: [{ date: '', duration: 0 }],
  });
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchWorkshops();
  }, []);

  const fetchWorkshops = async () => {
    try {
      const response = await api.get<{ data: Workshop[] }>('/workshops/all');
      setWorkshops(response.data.data);
    } catch (error) {
      console.error('Error fetching workshops:', error);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = (workshop?: Workshop) => {
    if (workshop) {
      setEditingWorkshop(workshop);
      setFormData({
        ...workshop,
        earlyBirdDate: workshop.earlyBirdDate.split('T')[0],
        workshopDates: workshop.workshopDates.map((date: IWorkshopDate) => ({
          ...date,
          date: date.date.split('T')[0],
        })),
        active: workshop.active ?? true,
        priority: workshop.priority ?? 0
      });
    } else {
      setEditingWorkshop(null);
      setFormData({
        title: '',
        description: '',
        location: '',
        category: CATEGORY_OPTIONS[0].value,
        level: 'beginner',
        participantLimit: 0,
        price: 0,
        earlyBirdDate: '',
        earlyBirdDiscountPercentage: 0,
        image: '',
        shouldHidePrice: false,
        active: true,
        priority: 0,
        workshopDates: [{ date: '', duration: 0 }],
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingWorkshop(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = editingWorkshop
        ? `/workshops/${editingWorkshop._id}`
        : '/workshops';
      const method = editingWorkshop ? 'put' : 'post';

      await api[method]<{ data: Workshop }>(url, formData);
      fetchWorkshops();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving workshop:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('admin.workshops.confirmDelete'))) {
      try {
        await api.delete(`/workshops/${id}`);
        fetchWorkshops();
      } catch (error) {
        console.error('Error deleting workshop:', error);
      }
    }
  };

  const handleDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    }
  }, []);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(files[0]);
    }
  }, []);

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    try {
      setIsUploading(true);
      
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = async () => {
        const base64String = reader.result as string;
        
        try {
          const response = await api.post<{ url: string }>('/files/upload', {
            image: base64String
          });

          if (response.data && response.data.url) {
            setFormData(prev => ({ ...prev, image: response.data.url }));
          } else {
            throw new Error('No image URL received from server');
          }
        } catch (error: any) {
          console.error('Error uploading file:', error);
        } finally {
          setIsUploading(false);
        }
      };

      reader.onerror = () => {
        setIsUploading(false);
      };
    } catch (error) {
      console.error('Error processing file:', error);
      setIsUploading(false);
    }
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">{t('admin.tabs.workshops')}</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          {t('admin.workshops.addNew')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.workshops.title')}</TableCell>
              <TableCell>{t('admin.workshops.category')}</TableCell>
              <TableCell>{t('admin.workshops.level')}</TableCell>
              <TableCell>{t('admin.workshops.price')}</TableCell>
              <TableCell>{t('admin.workshops.participants')}</TableCell>
              <TableCell>{t('admin.workshops.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workshops
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((workshop) => (
                <TableRow key={workshop._id}>
                  <TableCell>{workshop.title}</TableCell>
                  <TableCell>{workshop.category}</TableCell>
                  <TableCell>{workshop.level}</TableCell>
                  <TableCell>{workshop.price}</TableCell>
                  <TableCell>{workshop.participantLimit}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenModal(workshop)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(workshop._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={workshops.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingWorkshop ? t('admin.workshops.edit') : t('admin.workshops.addNew')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('admin.workshops.title')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('admin.workshops.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('admin.workshops.location')}
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('admin.workshops.category')}
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  required
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('admin.workshops.level')}
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value as any })}
                  required
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.workshops.participantLimit')}
                  value={formData.participantLimit}
                  onChange={(e) => setFormData({ ...formData, participantLimit: Number(e.target.value) })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.workshops.price')}
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label={t('admin.workshops.earlyBirdDate')}
                  value={formData.earlyBirdDate}
                  onChange={(e) => setFormData({ ...formData, earlyBirdDate: e.target.value })}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.workshops.earlyBirdDiscount')}
                  value={formData.earlyBirdDiscountPercentage}
                  onChange={(e) => setFormData({ ...formData, earlyBirdDiscountPercentage: Number(e.target.value) })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.workshops.priority')}
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: Number(e.target.value) })}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={t('admin.workshops.active')}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    onChange={handleFileSelect}
                  />
                  <label htmlFor="image-upload">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {isUploading ? (
                        <CircularProgress />
                      ) : (
                        <>
                          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                          <Typography variant="body1" gutterBottom>
                            {t('admin.workshops.dragAndDrop')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('admin.workshops.orClickToUpload')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </Box>
                {formData.image && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('admin.workshops.selectedImage')}
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">{t('admin.workshops.workshopDates')}</Typography>
                {formData.workshopDates.map((date, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        type="date"
                        label={t('admin.workshops.date')}
                        value={date.date}
                        onChange={(e) => {
                          const newDates = [...formData.workshopDates];
                          newDates[index].date = e.target.value;
                          setFormData({ ...formData, workshopDates: newDates });
                        }}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('admin.workshops.duration')}
                        value={date.duration}
                        onChange={(e) => {
                          const newDates = [...formData.workshopDates];
                          newDates[index].duration = Number(e.target.value);
                          setFormData({ ...formData, workshopDates: newDates });
                        }}
                        required
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                      {formData.workshopDates.length > 1 && (
                        <IconButton
                          color="error"
                          onClick={() => {
                            const newDates = formData.workshopDates.filter((_, i) => i !== index);
                            setFormData({ ...formData, workshopDates: newDates });
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
                <Button
                  variant="outlined"
                  onClick={() => setFormData({
                    ...formData,
                    workshopDates: [...formData.workshopDates, { date: '', duration: 0 }]
                  })}
                >
                  {t('admin.workshops.addDate')}
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingWorkshop ? t('common.save') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}; 